import { useTranslations } from '@vocab/react';
import { Actions, Box, Button, Stack } from 'braid-design-system';
import { useDispatch } from 'react-redux';

import {
  START_RESUME,
  FINISH_RESUME,
  DRAWER_CARDS,
} from 'src/components/Router';
import { Link } from 'src/components/Router/Link';
import { useCard } from 'src/components/Router/useCard';
import { useMode } from 'src/components/Router/useMode';
import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { EDIT } from 'src/consts';
import { analyticsClient } from 'src/modules/analytics';
import { useAppConfig } from 'src/modules/appConfig';
import type { DrawerCard } from 'src/types';

import { Validation } from '../Validation/Validation';
import { updateCreateStep } from '../actionCreators';
import { resumeLimit } from '../consts';
import { useGenerateResumeFromProfile } from '../data';
import { CreateStep } from '../reducer';
import { useGetCreateResumeRoutes } from '../routes';
import type { CreateResumeRoute } from '../types';

import translations from './.vocab';
import { InvalidSectionButton } from './InvalidSectionButton/InvalidSectionButton';
import { PageErrorMessage } from './PageErrorMessage/PageErrorMessage';

interface PageProps {
  name: CreateResumeRoute;
  marginBottom: React.ReactText;
  resumeCount: number;
  children: React.ReactElement;
}

export const Page = ({
  name,
  marginBottom,
  resumeCount,
  children,
}: PageProps) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const navigate = useNavigateRoute();
  const card = useCard();
  const mode = useMode(card);
  const { zone, language: languageCode } = useAppConfig();
  const isEditing =
    mode === EDIT &&
    name === card &&
    !DRAWER_CARDS.includes(card as DrawerCard);

  const createResumeRoutes = useGetCreateResumeRoutes();
  const [generateResumeFromProfile] = useGenerateResumeFromProfile();
  const currentIndex = createResumeRoutes.indexOf(name);
  const isLastPage = currentIndex === createResumeRoutes.length - 1;
  const navigateTo = isLastPage
    ? FINISH_RESUME
    : createResumeRoutes[currentIndex + 1];

  const validPageOnClick = () => {
    if (name === FINISH_RESUME) {
      window.scrollTo(0, 0);

      if (resumeCount < resumeLimit) {
        dispatch(updateCreateStep(CreateStep.RESUME_CREATED));
        setTimeout(
          () => generateResumeFromProfile({ zone, languageCode }),
          500,
        );
      } else {
        dispatch(updateCreateStep(CreateStep.RESUME_LIMIT_REACHED));
      }
    } else {
      navigate({
        card: navigateTo,
      });
    }
  };

  const getButtonText = () => {
    if (name === START_RESUME) {
      return t('Get started');
    }

    if (name === FINISH_RESUME) {
      return t('Create Resumé');
    }

    return t('Next');
  };

  const dataAutomation = `${name}-${
    name === START_RESUME || name === FINISH_RESUME ? '' : 'next-'
  }button`;
  const linkParams = {
    component: Button,
    color: 'blue',
    onClick: () => {
      if (name === START_RESUME) {
        analyticsClient.getStartedButtonClicked();
      }

      validPageOnClick();
    },
    data: { automation: dataAutomation },
  };
  const buttonText = getButtonText();
  const showLink = !isEditing && name !== FINISH_RESUME;
  const showValidationSummary = !isEditing && name === FINISH_RESUME;

  return (
    <Box style={{ marginBottom }}>
      <Stack space="large">
        {children}
        {name === FINISH_RESUME ? (
          <Validation renderOnInvalid name={name}>
            <PageErrorMessage />
          </Validation>
        ) : null}
        <Actions>
          {showLink && <Link {...linkParams}>{buttonText}</Link>}
          {showValidationSummary && (
            <>
              <Validation renderOnInvalid={true} name={name}>
                <InvalidSectionButton name={name}>
                  {buttonText}
                </InvalidSectionButton>
              </Validation>
              <Validation renderOnInvalid={false} name={name}>
                <Link {...linkParams}>{buttonText}</Link>
              </Validation>
            </>
          )}
        </Actions>
      </Stack>
    </Box>
  );
};
